import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { OrganizationApi } from 'entities/Organization'
import { PhoneCheckFirstModalActions } from 'pages/settings/pages/billing/pages/upgrade/ui/PhoneCheckFirstModalContent/PhoneCheckFirstModalActions'
import { PhoneCheckFirstModalContent } from 'pages/settings/pages/billing/pages/upgrade/ui/PhoneCheckFirstModalContent/PhoneCheckFirstModalContent'
import { PhoneCheckSecondModalActions } from 'pages/settings/pages/billing/pages/upgrade/ui/PhoneCheckSecondModalContent/PhoneCheckSecondModalActions'
import { PhoneCheckSecondModalContent } from 'pages/settings/pages/billing/pages/upgrade/ui/PhoneCheckSecondModalContent/PhoneCheckSecondModalContent'

export type PhoneCheckrLookupOption = 'all' | 'new'

export class PhoneCheckrStore {
  private _modalId = ''
  private _lookupOption: PhoneCheckrLookupOption = 'all'

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get lookupOption() {
    return this._lookupOption
  }

  get isActive() {
    return this._entities.organizationStore.isPhoneCheckerEnabled
  }

  get phoneChecker() {
    return this._entities.organizationStore.phoneChecker
  }

  setLookupOption = (option: PhoneCheckrLookupOption) => {
    this._lookupOption = option
  }

  openFirstModal = () => {
    this._modalId = nanoid()

    modalStore.addModal({
      id: this._modalId,
      width: 540,
      title: 'Details',
      showCloseButton: false,
      ModalActions: PhoneCheckFirstModalActions,
      ModalContent: PhoneCheckFirstModalContent,
      paddingContent: '8px 24px 12px 24px',
      ModalContentProps: {
        store: this,
      },
    })
  }

  openSecondModal = () => {
    modalStore.removeModal(this._modalId)
    this._modalId = nanoid()

    modalStore.addModal({
      id: this._modalId,
      width: 540,
      title: 'Details',
      showCloseButton: false,
      ModalActions: PhoneCheckSecondModalActions,
      ModalContent: PhoneCheckSecondModalContent,
      paddingContent: '8px 24px 18px 24px',
      ModalContentProps: {
        store: this,
      },
    })
  }

  closeModal = () => {
    modalStore.removeModal(this._modalId)
  }

  backToFirstModal = () => {
    modalStore.removeModal(this._modalId)
    this.openFirstModal()
  }

  handleSwitchChange = async (value: boolean) => {
    if (!value) {
      await this.handlePhoneCheckrSwitchChange(value, false)
    } else {
      this.openSecondModal()
    }
  }

  handlePhoneCheckrSwitchChange = async (value: boolean, checkExistingContacts: boolean) => {
    try {
      if (value) {
        const { data } = await OrganizationApi.enableOrganizationPhoneCheckr({
          check_existing_contacts: checkExistingContacts,
        })
        this._entities.organizationStore.setPhoneChecker(data)

        const toastDesc = checkExistingContacts
          ? 'Contact validation is in progress. Please be patient, as processing time depends on the volume of phone numbers.'
          : ''

        showToast({
          type: 'success',
          title: 'PhoneCheckr activated',
          desc: toastDesc,
        })
      } else {
        const { data } = await OrganizationApi.disableOrganizationPhoneCheckr()
        this._entities.organizationStore.setPhoneChecker({
          ...data,
          enabled_at: data.enabled_at || '',
        })
        showToast({
          type: 'success',
          title: 'PhoneCheckr disabled',
        })
      }
    } catch (e) {
      logger.error(e)
      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
    }
  }

  activatePhoneCheckr = async () => {
    try {
      const checkExistingContacts = this._lookupOption === 'all'

      await this.handlePhoneCheckrSwitchChange(true, checkExistingContacts)

      this.setLookupOption('all')
      this.closeModal()
    } catch (e) {
      logger.error(e)
      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
    }
  }
}
